import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Stack,
  Box,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PublishIcon from '@mui/icons-material/Publish';
import { toast } from 'react-toastify';
import { getPages, listBlogPosts, publishAllBlogs } from './seoApi';

const STATUS_META = {
  default: { label: 'Default', color: 'default' },
  published: { label: 'Published', color: 'success' },
  draft: { label: 'Draft', color: 'info' },
  dirty: { label: 'Unpublished changes', color: 'warning' },
};

export default function SeoDashboard() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishingAll, setPublishingAll] = useState(false);

  useEffect(() => {
    (async () => {
      const [pagesRes, postsRes] = await Promise.all([getPages(), listBlogPosts()]);
      if (pagesRes?.status === 200) setPages(pagesRes.data.pages || []);
      else toast.error(pagesRes?.data?.message || 'Failed to load pages');
      if (postsRes?.status === 200) setPosts(postsRes.data.posts || []);
      setLoading(false);
    })();
  }, []);

  const draftCount = posts.filter((p) => !p.published).length;

  const handlePublishAll = async () => {
    if (
      !window.confirm(
        `Publish all blog posts? ${draftCount} draft${draftCount === 1 ? '' : 's'} will go live on the public site.`
      )
    )
      return;
    setPublishingAll(true);
    const res = await publishAllBlogs();
    if (res?.status === 200) {
      const { publishedCount = 0, sluggedCount = 0 } = res.data || {};
      const sluggedNote = sluggedCount
        ? ` and generated ${sluggedCount} missing URL slug${sluggedCount === 1 ? '' : 's'}`
        : '';
      toast.success(`Published ${publishedCount} post${publishedCount === 1 ? '' : 's'}${sluggedNote}`);
      const postsRes = await listBlogPosts();
      if (postsRes?.status === 200) setPosts(postsRes.data.posts || []);
    } else {
      toast.error(res?.data?.message || 'Failed to publish all posts');
    }
    setPublishingAll(false);
  };

  // Preserve registry order but group by section.
  const groups = pages.reduce((acc, p) => {
    (acc[p.group] = acc[p.group] || []).push(p);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">SEO / Pages</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/dashboard/seo/redirects')}>
            Redirects
          </Button>
          <Button variant="outlined" onClick={() => navigate('/dashboard/seo/history')}>
            History
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        {Object.keys(groups).map((group) => (
          <Card key={group} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ px: 3, pt: 2 }}>
              {group}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell>Path</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups[group].map((p) => {
                    const s = STATUS_META[p.status] || STATUS_META.default;
                    return (
                      <TableRow key={p.path} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {p.label}
                            {p.locked && <LockIcon fontSize="small" color="disabled" titleAccess="Locked page" />}
                            {p.compliance && <Chip size="small" label="Legal" variant="outlined" color="warning" />}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{p.path}</TableCell>
                        <TableCell>
                          <Chip size="small" label={s.label} color={s.color} />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/dashboard/seo/edit?path=${encodeURIComponent(p.path)}`)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ))}

        <Card sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, pt: 2 }}>
            <Typography variant="h6">Blog Posts</Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PublishIcon />}
              disabled={publishingAll || draftCount === 0}
              onClick={handlePublishAll}
            >
              {publishingAll ? 'Publishing…' : `Publish All${draftCount ? ` (${draftCount})` : ''}`}
            </Button>
          </Stack>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Post</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No blog posts yet.
                    </TableCell>
                  </TableRow>
                )}
                {posts.map((p) => (
                  <TableRow key={p._id} hover>
                    <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.title}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                      /blog/{p.slug || p._id}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.published ? 'Published' : 'Draft'}
                        color={p.published ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {p.updatedAt || p.modifiedOn
                        ? new Date(p.updatedAt || p.modifiedOn).toLocaleString()
                        : new Date(p.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => navigate(`/dashboard/seo/blog?id=${p._id}`)}
                      >
                        Edit SEO
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        </>
      )}
    </Container>
  );
}
